'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { CheckCircle, XCircle, WarningCircle } from '@phosphor-icons/react';
import { authApi } from '../services/api';
import { Footer } from './components/Footer';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage(t('auth.invalidVerificationLink'));
        return;
      }

      try {
        const response = await authApi.verifyEmail(token);
        if (response.code === 0) {
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMessage(response.message || t('auth.verifyFailed'));
        }
      } catch (err: unknown) {
        setStatus('error');
        const msg = err instanceof Error ? err.message : t('auth.verifyFailed');
        setErrorMessage(msg);
      }
    };

    verifyEmail();
  }, [token, t]);

  return (
    <div className="min-h-[100dvh] bg-zinc-50 flex items-center justify-center p-6">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card variant="elevated" className="overflow-hidden">
          <CardContent className="p-8 text-center">
            {status === 'loading' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="w-16 h-16 mx-auto mb-6">
                  <div className="w-full h-full border-4 border-zinc-200 border-t-emerald-500 rounded-full animate-spin" />
                </div>
                <h2 className="text-xl font-semibold text-zinc-900 mb-2">
                  {t('auth.verifying')}
                </h2>
                <p className="text-zinc-500">
                  {t('auth.verifyingDesc')}
                </p>
              </motion.div>
            )}

            {status === 'success' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center"
                >
                  <CheckCircle className="w-10 h-10 text-emerald-600" weight="fill" />
                </motion.div>

                <h2 className="text-2xl font-semibold text-zinc-900 tracking-tight mb-2">
                  {t('auth.verifySuccess')}
                </h2>
                <p className="text-zinc-500 mb-6">
                  {t('auth.verifySuccessDesc')}
                </p>

                <div className="space-y-3">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full"
                    onClick={() => navigate('/login')}
                  >
                    {t('auth.goToLogin')}
                  </Button>
                </div>
              </motion.div>
            )}

            {status === 'error' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center"
                >
                  <XCircle className="w-10 h-10 text-red-600" weight="fill" />
                </motion.div>

                <h2 className="text-2xl font-semibold text-zinc-900 tracking-tight mb-2">
                  {t('auth.verifyFailed')}
                </h2>
                <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <WarningCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800 text-left">
                      {errorMessage || t('auth.verifyFailedDesc')}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <Link to="/login">
                    <Button variant="primary" size="lg" className="w-full">
                      {t('auth.backToLogin')}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button variant="ghost" size="lg" className="w-full mt-3">
                      {t('auth.resendVerification')}
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>

        <Footer />
      </motion.div>
    </div>
  );
}
